import * as THREE from 'three';
import { Brush, Evaluator, SUBTRACTION, ADDITION, INTERSECTION } from 'three-bvh-csg';

export type CSGOperation = 'Union' | 'Subtract' | 'Intersect';

export class CSGHelper {
    private static evaluator = new Evaluator();

    /**
     * Perform boolean operation: A - B (Subtract), A + B (Union), A & B (Intersect)
     * @param target The base object (e.g., Wall)
     * @param tool The modifying object (e.g., Void Form)
     * @param operation Operation type
     */
    static performOperation(
        target: THREE.Mesh,
        tool: THREE.Mesh,
        operation: CSGOperation = 'Subtract'
    ): THREE.Mesh {

        // Convert to Brushes (CSG optimized meshes)
        const targetBrush = this.toBrush(target);
        const toolBrush = this.toBrush(tool);

        // Map operation
        let opValue = SUBTRACTION;
        if (operation === 'Union') opValue = ADDITION;
        if (operation === 'Intersect') opValue = INTERSECTION;

        // Evaluate
        // useGroups: false merges materials if needed, simpler for initial implementation
        this.evaluator.useGroups = true;
        const resultBrush = this.evaluator.evaluate(targetBrush, toolBrush, opValue);

        // Output result
        const resultMesh = new THREE.Mesh(resultBrush.geometry, resultBrush.material);

        // Preserve original position/matrix (CSG result is usually world-space or local-space depending on setup)
        // Here we assume evaluate returns world-aligned geometry effectively replacing the original
        resultMesh.castShadow = target.castShadow;
        resultMesh.receiveShadow = target.receiveShadow;

        return resultMesh;
    }

    /**
     * Subtract multiple tools from one target
     */
    static subtract(target: THREE.Mesh, tools: THREE.Mesh[]): THREE.Mesh {
        if (tools.length === 0) return target;

        let currentBrush = this.toBrush(target);
        const toolBrushes = tools.map(t => this.toBrush(t));

        for (const toolBrush of toolBrushes) {
            currentBrush = this.evaluator.evaluate(currentBrush, toolBrush, SUBTRACTION);
        }

        const resultMesh = new THREE.Mesh(currentBrush.geometry, currentBrush.material);
        return resultMesh;
    }

    private static toBrush(mesh: THREE.Mesh): any {
        const brush = new Brush(mesh.geometry, mesh.material);
        brush.position.copy(mesh.position);
        brush.rotation.copy(mesh.rotation);
        brush.scale.copy(mesh.scale);
        brush.updateMatrixWorld();
        brush.prepareGeometry();
        return brush;
    }
}
